import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  FileText, Download, RefreshCw, Shield, Activity, Clock,
  DollarSign, AlertTriangle, Users, ChevronLeft, ChevronRight,
  FileSpreadsheet, FileDown, BarChart3, CheckCircle,
} from 'lucide-react';
import api from '../../lib/api';

const REPORT_ICONS = {
  service_delivery_audit: Activity,
  staff_activity: Users,
  timesheet_vs_invoice: Clock,
  funding_utilisation: DollarSign,
  compliance: Shield,
  exceptions: AlertTriangle,
};

const REPORT_DESCRIPTIONS = {
  service_delivery_audit: 'Full participant-staff-session chain with service links and invoice references',
  staff_activity: 'Staff workload across group sessions and individual shifts',
  timesheet_vs_invoice: 'Discrepancy analysis between timesheets and invoice linkages',
  funding_utilisation: 'Per-participant funding plan utilisation and billing status',
  compliance: 'Compliance violations and risk pattern detection',
  exceptions: 'Service delivery exceptions requiring manual review',
};

const PAGE_SIZE = 20;

export default function AuditReportingPage() {
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [filters, setFilters] = useState({ start_date: '', end_date: '', state_id: '', branch_id: '', session_type: '' });
  const [report, setReport] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [page, setPage] = useState(0);
  const [snapshotLoading, setSnapshotLoading] = useState(true);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await api.get('/admin/audit-reports/types');
      if (res.data?.data) {
        setReportTypes(res.data.data);
        if (res.data.data.length > 0) setSelectedType(res.data.data[0].key);
      }
    } catch (e) {
      console.error('Failed to load report types', e);
    }
  }, []);

  const fetchSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    try {
      const res = await api.get('/admin/audit-reports/audit-snapshot');
      if (res.data?.data) setSnapshot(res.data.data);
    } catch (e) {
      console.error('Failed to load snapshot', e);
    }
    setSnapshotLoading(false);
  }, []);

  useEffect(() => {
    fetchTypes();
    fetchSnapshot();
  }, [fetchTypes, fetchSnapshot]);

  const runReport = async () => {
    if (!selectedType) return;
    setLoading(true);
    setReport(null);
    setPage(0);
    try {
      const res = await api.post('/admin/audit-reports/run', { reportType: selectedType, filters });
      if (res.data?.data) {
        setReport(res.data.data);
        toast.success(`Report generated: ${res.data.data.total_rows} rows`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail?.error?.message || e.response?.data?.detail?.message || 'Report generation failed';
      toast.error(msg);
    }
    setLoading(false);
  };

  const exportReport = async (format) => {
    if (!selectedType) return;
    setExporting(format);
    try {
      const res = await api.post(`/admin/audit-reports/export/${format}`, { reportType: selectedType, filters }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'csv' ? 'csv' : 'pdf';
      a.download = `audit_${selectedType}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (e) {
      toast.error(`${format.toUpperCase()} export failed`);
    }
    setExporting('');
  };

  const resetFilters = () => {
    setFilters({ start_date: '', end_date: '', state_id: '', branch_id: '', session_type: '' });
    setReport(null);
    setPage(0);
  };

  const rows = report?.rows || [];
  const columns = report?.columns || [];
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pagedRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="space-y-6 p-1" data-testid="audit-reporting-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="audit-reporting-title">Audit Reporting</h1>
          <p className="text-sm text-muted-foreground mt-1">NDIS audit-ready reports with full traceability. Export as CSV or PDF.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSnapshot} data-testid="refresh-snapshot-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Audit Snapshot Cards */}
      {snapshot && !snapshotLoading && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="audit-snapshot-panel">
          <SnapshotCard label="Services Delivered" value={snapshot.total_services_delivered} icon={Activity} />
          <SnapshotCard label="Staff Assignments" value={snapshot.total_staff_assignments} icon={Users} />
          <SnapshotCard label="Service Links" value={snapshot.total_service_links} icon={FileText} />
          <SnapshotCard label="Capture Rate" value={`${snapshot.capture_rate}%`} icon={BarChart3} accent={snapshot.capture_rate >= 80 ? 'text-emerald-600' : 'text-amber-600'} />
          <SnapshotCard label="Compliance Issues" value={snapshot.compliance_violations} icon={Shield} accent={snapshot.compliance_violations > 0 ? 'text-red-600' : 'text-emerald-600'} />
        </div>
      )}

      <Separator />

      {/* Report Selector + Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Report Type Selector */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Report Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {reportTypes.map((rt) => {
              const Icon = REPORT_ICONS[rt.key] || FileText;
              return (
                <button
                  key={rt.key}
                  data-testid={`report-type-${rt.key}`}
                  onClick={() => { setSelectedType(rt.key); setReport(null); setPage(0); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${selectedType === rt.key ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted text-muted-foreground'}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {rt.title}
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Filters + Actions + Results */}
        <div className="lg:col-span-3 space-y-4">
          {/* Selected report description */}
          {selectedType && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border">
              <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{reportTypes.find(r => r.key === selectedType)?.title}</p>
                <p className="text-xs text-muted-foreground">{REPORT_DESCRIPTIONS[selectedType]}</p>
              </div>
            </div>
          )}

          {/* Filters Row */}
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Date From</label>
                  <Input
                    type="date"
                    data-testid="filter-date-from"
                    value={filters.start_date}
                    onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Date To</label>
                  <Input
                    type="date"
                    data-testid="filter-date-to"
                    value={filters.end_date}
                    onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">State</label>
                  <Input
                    data-testid="filter-state"
                    placeholder="State ID"
                    value={filters.state_id}
                    onChange={(e) => setFilters(f => ({ ...f, state_id: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
                  <Input
                    data-testid="filter-branch"
                    placeholder="Branch ID"
                    value={filters.branch_id}
                    onChange={(e) => setFilters(f => ({ ...f, branch_id: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Session Type</label>
                  <Select value={filters.session_type} onValueChange={(v) => setFilters(f => ({ ...f, session_type: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="filter-session-type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="group">Group</SelectItem>
                      <SelectItem value="centre_based">Centre Based</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <Button onClick={runReport} disabled={loading || !selectedType} data-testid="run-report-btn" size="sm">
                  {loading ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-1" />}
                  {loading ? 'Generating...' : 'Run Report'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportReport('csv')} disabled={!selectedType || exporting === 'csv'} data-testid="export-csv-btn">
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportReport('pdf')} disabled={!selectedType || exporting === 'pdf'} data-testid="export-pdf-btn">
                  <FileDown className="h-4 w-4 mr-1" />
                  {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
                </Button>
                <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filters-btn">Reset</Button>
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12" data-testid="report-loading">
              <div className="text-center space-y-2">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Generating report...</p>
              </div>
            </div>
          )}

          {/* Report Results */}
          {report && !loading && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">{report.title}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" data-testid="report-row-count">{report.total_rows} rows</Badge>
                    {report.discrepancy_count !== undefined && report.discrepancy_count > 0 && (
                      <Badge variant="destructive" data-testid="report-discrepancy-count">{report.discrepancy_count} discrepancies</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {rows.length === 0 ? (
                  <div className="text-center py-8" data-testid="report-empty-state">
                    <CheckCircle className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">No data found for the selected filters.</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto rounded-md border">
                      <Table data-testid="report-results-table">
                        <TableHeader>
                          <TableRow>
                            {columns.map((col) => (
                              <TableHead key={col} className="text-xs whitespace-nowrap">{col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pagedRows.map((row, idx) => (
                            <TableRow key={idx}>
                              {columns.map((col) => (
                                <TableCell key={col} className="text-xs max-w-[200px] truncate" title={String(row[col] ?? '')}>
                                  {renderCell(col, row[col])}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t" data-testid="report-pagination">
                        <p className="text-xs text-muted-foreground">
                          Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, rows.length)} of {rows.length}
                        </p>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} data-testid="page-prev-btn">
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-xs px-2">{page + 1} / {totalPages}</span>
                          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} data-testid="page-next-btn">
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Empty state - no report run yet */}
          {!report && !loading && (
            <div className="text-center py-12 border rounded-lg bg-muted/20" data-testid="no-report-state">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Select a report type and click "Run Report" to generate.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SnapshotCard({ label, value, icon: Icon, accent }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground/50" />
        </div>
        <p className={`text-xl font-semibold mt-1 tabular-nums ${accent || ''}`} data-testid={`snapshot-${label.toLowerCase().replace(/\s/g, '-')}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function renderCell(col, value) {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground/50">-</span>;

  if (col === 'discrepancy') {
    return value === 'YES' ? <Badge variant="destructive" className="text-xs">YES</Badge> : <Badge variant="secondary" className="text-xs">NO</Badge>;
  }
  if (col === 'severity') {
    const colors = { CRITICAL: 'bg-red-600 text-white', HIGH: 'bg-orange-500 text-white', MEDIUM: 'bg-yellow-500 text-black', LOW: 'bg-slate-500 text-white' };
    return <Badge className={`text-xs ${colors[value] || ''}`}>{value}</Badge>;
  }
  if (col === 'review_status' || col === 'service_link_status') {
    const colors = { ready: 'bg-emerald-100 text-emerald-800', completed: 'bg-blue-100 text-blue-800', manual_review: 'bg-amber-100 text-amber-800', pending: 'bg-slate-100 text-slate-800', no_link: 'bg-red-100 text-red-800' };
    return <Badge className={`text-xs ${colors[value] || 'bg-slate-100'}`}>{value}</Badge>;
  }
  if (col === 'attendance_status') {
    return <Badge variant="outline" className="text-xs">{value}</Badge>;
  }
  if (col.includes('pct') || col.includes('rate')) {
    return <span className="font-mono text-xs">{value}%</span>;
  }

  return <span>{String(value)}</span>;
}
